import { useEffect, useMemo, useRef } from "react";

import { useChatPanelStore } from "@/store/chatPanelStore";

import { useUser } from "./useUser";

// Pre-create audio element so it can be unlocked on first user interaction
let agentStartAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;

if (typeof window !== "undefined") {
  agentStartAudio = new Audio("/assets/audio/agent-start.wav");

  const unlock = () => {
    if (audioUnlocked || !agentStartAudio) return;
    agentStartAudio.muted = true;
    agentStartAudio.play().then(() => {
      agentStartAudio!.pause();
      agentStartAudio!.muted = false;
      agentStartAudio!.currentTime = 0;
      audioUnlocked = true;
    }).catch(() => {});
    document.removeEventListener("pointerdown", unlock);
  };
  document.addEventListener("pointerdown", unlock, { once: true });
}

export function usePodStatus() {
  const { user, isLoading } = useUser();
  // Initialize to true so the sound only plays on a witnessed transition
  // (e.g. provisioning → running), not on mount when already running.
  const wasRunningRef = useRef(true);

  const status = useMemo(() => {
    if (isLoading || !user) {
      return { isLoading: true, isProvisioning: false, isRunning: false, isError: false, podState: null, podStateDetail: null };
    }

    const isProvisioning =
      user.containerStatus === "pending" ||
      user.containerStatus === "provisioning" ||
      user.podState === "not_found" ||
      user.podState === "starting";

    return {
      isLoading: false,
      isProvisioning,
      isRunning: user.podState === "running" && user.containerStatus === "running",
      isError: user.podState === "error",
      podState: user.podState,
      // Backend-provided WHY (queued: high demand, mount failure, quarantine
      // reason) — rendered as a muted second line under the status banner.
      podStateDetail: user.podStateDetail ?? null,
    };
  }, [user, isLoading]);

  useEffect(() => {
    if (status.isRunning && !wasRunningRef.current) {
      if (agentStartAudio && !useChatPanelStore.getState().soundsMuted) {
        agentStartAudio.currentTime = 0;
        agentStartAudio.play().catch(() => {});
      }
    }
    wasRunningRef.current = status.isRunning;
  }, [status.isRunning]);

  return status;
}
